import { Check } from '@timelyapp/icons/solid-icons';
import classNames from 'classnames';
import React from 'react';
import styles from './ColorPicker.module.scss';
import { isContrastReadable, prefixHexColor } from './color-picker.utils';

export type ColorPickerSize = 'small' | 'medium' | 'large';

export interface ColorPickerProps {
  /** Class name for the container. */
  className?: string;
  /** Hex value of the currently selected color (with or without `#`). */
  color?: string;
  /** Array of hex color strings to display as options. */
  colors?: ReadonlyArray<string>;
  /** Max width for the container. */
  maxWidth?: number;
  /** Called when a color swatch is clicked. */
  onChange: (color: string) => void;
  /** Size of the color circles. @default 'medium' */
  size?: ColorPickerSize;
}

const EMPTY_ARRAY: ReadonlyArray<string> = [];

export const ColorPicker = React.forwardRef<HTMLUListElement, ColorPickerProps>(
  ({ className, color, colors = EMPTY_ARRAY, maxWidth, onChange, size = 'medium' }, ref) => {
    const regex = new RegExp(`^#?(${color})$`, 'i');

    return (
      <ul
        ref={ref}
        className={classNames(className, styles.colors, styles[`size-${size}`])}
        style={{ maxWidth }}
      >
        {colors.map(c => {
          const hexColor = prefixHexColor(c);
          const isSelected = regex.test(hexColor);
          return (
            <li
              key={hexColor}
              className={classNames(styles.color, {
                [styles.selected]: isSelected,
                [styles.light]: !isContrastReadable('#fff', hexColor),
              })}
              style={{
                backgroundColor: hexColor,
                outlineColor: hexColor,
              }}
              onClick={() => onChange(hexColor)}
            >
              {isSelected && <Check />}
            </li>
          );
        })}
      </ul>
    );
  }
);

ColorPicker.displayName = 'ColorPicker';
